import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Descriptions, Tabs, Button, Space, Image, Spin, message, Row, Col, Typography, Statistic, Modal, Tooltip, Input } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, CarOutlined, AppstoreOutlined, BankOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Block } from '@/types/resources/property';
import { getBlockById, updateBlock, deleteBlock, forceDeleteBlock, getApartmentUnitsByBlock, updateApartmentUnit } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';
import { getBlockStats, hasWorkspaceStats } from '@/utils/workspaceStats';
import ActivityLogList from '@/components/common/ActivityLogList';
import { BlockFormModal } from '@/components/features/resources/properties/BlockFormModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { ResidentListTab } from '@/components/tab/ResidentListTab';
import { Resident } from '@/types/resources/resident';

const { Title } = Typography;

const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export const BlockDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('properties');
  const { firestoreUser, workspaceStats } = useAuth();
  const queryClient = useQueryClient();
  const blockFromState = location.state?.block as Block | undefined;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<Error & { dependencies?: any[] } | undefined>(undefined);
  const [showResidentSelector, setShowResidentSelector] = useState(false);
  const [selectedApartmentUnit, setSelectedApartmentUnit] = useState<{ id: string; number: string; floor: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: block, isLoading, error } = useQuery({
    queryKey: ['blocks', id, firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getBlockById(id!, firestoreUser);
    },
    enabled: !!id && !!firestoreUser && !blockFromState,
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Fetch apartment units for this block
  const { data: apartmentUnitsData, isLoading: apartmentUnitsLoading, error: apartmentUnitsError } = useQuery({
    queryKey: ['apartmentUnitsByBlock', id, firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser || !id) throw new Error('User not authenticated or no block ID');
      return getApartmentUnitsByBlock(id, firestoreUser);
    },
    enabled: !!id && !!firestoreUser,
    staleTime: API_STALE_TIME_MS,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Block> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateBlock(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('blocks.messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['blocks', id] });
      setIsModalVisible(false);
    },
    onError: (error: Error) => {
      message.error(t('blocks.messages.updateError'));
      console.error('Error updating block:', error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteBlock(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('blocks.messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['blocks'] });
      setShowDeleteModal(false);
      navigate('/resources/properties/blocks');
    },
    onError: (error: Error) => {
      if ((error as any).dependencies) {
        setDeleteError(error as any);
      } else {
        message.error(t('blocks.messages.deleteError'));
        setShowDeleteModal(false);
      }
      console.error('Error deleting block:', error);
    }
  });

  const forceDeleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return forceDeleteBlock(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('blocks.messages.forceDeleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['blocks'] });
      setShowDeleteModal(false);
      navigate('/resources/properties/blocks');
    },
    onError: (error: Error) => {
      message.error(t('blocks.messages.forceDeleteError'));
      setShowDeleteModal(false);
      console.error('Error force deleting block:', error);
    }
  });

  // Mutation for updating apartment unit with resident
  const updateApartmentUnitMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateApartmentUnit(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success('Resident assigned successfully');
      queryClient.invalidateQueries({ queryKey: ['apartmentUnitsByBlock', id] });
      setShowResidentSelector(false);
      setSelectedApartmentUnit(null);
    },
    onError: (error: Error) => {
      message.error('Failed to assign resident');
      console.error('Error updating apartment unit:', error);
    }
  });

  const blockData = blockFromState || block;

  // Handle error with useEffect to avoid render method issues
  useEffect(() => {
    if (error && !blockFromState) {
      message.error(t('blocks.messages.notFound'));
    }
  }, [error, blockFromState, t]);

  if (isLoading && !blockFromState) {
    return <Spin size="large" />;
  }

  if ((error || !blockData) && !blockFromState) {
    return <div>{t('blocks.messages.notFound')}</div>;
  }

  if (!blockData) {
    return null;
  }

  const handleEdit = () => {
    setIsModalVisible(true);
  };

  const handleDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleModalSubmit = async (values: { block: Partial<Block>; floors: any[] }): Promise<string> => {
    if (blockData?.id) {
      updateMutation.mutate({ id: blockData.id, data: values.block });
      return blockData.id; // Return existing ID for edit mode
    }
    throw new Error('Block not found');
  };

  // Handle resident selection for apartment unit
  const handleResidentSelect = (resident: Resident) => {
    if (selectedApartmentUnit) {
      updateApartmentUnitMutation.mutate({
        id: selectedApartmentUnit.id,
        data: {
          resident: {
            id: resident.id,
            name: resident.user?.displayName || resident.contacts?.[0]?.name || 'Unknown'
          },
          status: 'occupied'
        }
      });
    }
  };

  // Handle removing resident from apartment unit
  const handleRemoveResident = (apartmentUnitId: string) => {
    const apartmentUnit = apartmentUnitsData?.apartmentUnits.find(unit => unit.id === apartmentUnitId);
    const residentName = apartmentUnit?.resident?.name || 'Unknown';
    
    Modal.confirm({
      title: 'Remove Resident',
      content: `Are you sure you want to remove ${residentName} from this apartment unit?`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        updateApartmentUnitMutation.mutate({
          id: apartmentUnitId,
          data: {
            resident: undefined,
            status: 'vacant'
          }
        });
      }
    });
  };

  // Get apartment unit data for a specific floor and unit number
  const getApartmentUnitData = (floorName: string, unitNumber: string) => {
    if (!apartmentUnitsData?.apartmentUnits) return null;
    
    return apartmentUnitsData.apartmentUnits.find(unit => 
      unit.floor?.name === floorName && unit.number === unitNumber
    );
  };

  const items = [
    {
      key: 'overview',
      label: t('blocks.details.title'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('blocks.details.attributes.name')}>
              {blockData.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('blocks.details.attributes.description')}>
              {blockData.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('blocks.details.attributes.totalFloors')}>
              {blockData.floors?.length || 0}
            </Descriptions.Item>
          </Descriptions>

          {/* Block Statistics */}
          {hasWorkspaceStats(workspaceStats) && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ marginBottom: 16 }}>Block Statistics</h3>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic
                      title="Apartment Units"
                      value={getBlockStats(workspaceStats, blockData.id).totalApartmentUnits}
                      prefix={<AppstoreOutlined style={{ color: '#52c41a' }} />}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic
                      title="Parking Spots"
                      value={getBlockStats(workspaceStats, blockData.id).totalParkings}
                      prefix={<CarOutlined style={{ color: '#faad14' }} />}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic
                      title="Amenities"
                      value={getBlockStats(workspaceStats, blockData.id).totalAmenities}
                      prefix={<BankOutlined style={{ color: '#722ed1' }} />}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic
                      title="Residents"
                      value={getBlockStats(workspaceStats, blockData.id).totalResidents}
                      prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                    />
                  </Card>
                </Col>
              </Row>
            </div>
          )}

          {blockData.images && blockData.images.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3>{t('blocks.details.attributes.images')}</h3>
              <Image.PreviewGroup>
                <Space>
                  {blockData.images.map(image => (
                    <Image
                      key={image.id}
                      width={200}
                      src={image.url}
                      alt={image.name}
                    />
                  ))}
                </Space>
              </Image.PreviewGroup>
            </div>
          )}
        </Card>
      ),
    },
    {
      key: 'floors',
      label: t('blocks.details.attributes.floors'),
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ marginBottom: 8 }}>Floor Plan</h3>
            <p style={{ color: '#666', fontSize: '14px' }}>
              {t('floors.instruction')}
            </p>
          </div>

                    {/* Apartment Building Visualization */}
          {apartmentUnitsLoading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '40px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              border: '1px solid #e8e8e8'
            }}>
              <Spin size="large" />
              <span style={{ marginLeft: 16, color: '#666' }}>Loading apartment units...</span>
            </div>
          ) : apartmentUnitsError ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '40px',
              backgroundColor: '#fff2f0',
              borderRadius: '8px',
              border: '1px solid #ffccc7',
              color: '#cf1322'
            }}>
              <span>Failed to load apartment units. Please try refreshing the page.</span>
            </div>
          ) : (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column-reverse', // Show ground floor at bottom
            gap: '2px',
            border: '2px solid #d9d9d9',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: '#fafafa',
            maxHeight: '600px',
            overflowY: 'auto'
          }}>
            {blockData.floors?.map((floor, floorIndex) => (
              <div key={floorIndex} style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '8px',
                padding: '12px',
                backgroundColor: '#fff',
                borderRadius: '4px',
                border: '1px solid #e8e8e8'
              }}>
                {/* Floor Info and Actions Row */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #f0f0f0',
                  paddingBottom: '8px'
                }}>
                  {/* Floor Label */}
                  <div style={{ 
                    fontWeight: 'bold',
                    color: '#1890ff',
                    fontSize: '14px'
                  }}>
                    {t('units.fields.floor')}: {floor.name}
                  </div>
                  
                  {/* Floor Actions */}
                  <Button 
                    type="text" 
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => navigate(`/resources/properties/floors/${floor.slug}`)}
                  >
                    {t('common:actions.edit')}
                  </Button>
                </div>

                {/* Apartment Units Row */}
                <div style={{ 
                  display: 'flex', 
                  gap: '4px',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start'
                }}>
                  {floor.apartmentUnits?.map((unit, unitIndex) => {
                    const apartmentUnitData = getApartmentUnitData(floor.name, unit.number);
                    const isOccupied = apartmentUnitData?.resident;
                    // const isVacant = !isOccupied;
                    
                    return (
                      <Tooltip
                        key={unitIndex}
                        title={
                          <div>
                            <div><strong>Unit {unit.number}</strong></div>
                            <div>Floor: {floor.name}</div>
                            <div>Status: {apartmentUnitData?.status || 'unknown'}</div>
                            {isOccupied && (
                              <div>Resident: {apartmentUnitData.resident?.name}</div>
                            )}
                            <div>Click to {isOccupied ? 'remove resident' : 'assign resident'}</div>
                          </div>
                        }
                      >
                        <div
                          style={{
                            width: '50px',
                            height: '40px',
                            backgroundColor: isOccupied ? '#52c41a' : '#f0f0f0',
                            border: `2px solid ${isOccupied ? '#389e0d' : '#d9d9d9'}`,
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: '500',
                            color: isOccupied ? '#fff' : '#595959',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            position: 'relative',
                            flexShrink: 0
                          }}
                          onClick={() => {
                            if (isOccupied) {
                              // Remove resident
                              if (apartmentUnitData?.id) {
                                handleRemoveResident(apartmentUnitData.id);
                              }
                            } else {
                              // Assign resident
                              setSelectedApartmentUnit({
                                id: apartmentUnitData?.id || '',
                                number: unit.number,
                                floor: floor.name
                              });
                              setShowResidentSelector(true);
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                            e.currentTarget.style.borderColor = isOccupied ? '#ff4d4f' : '#1890ff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.borderColor = isOccupied ? '#389e0d' : '#d9d9d9';
                          }}
                        >
                          <div style={{ fontSize: '8px', lineHeight: '1' }}>
                            {unit.number}
                          </div>
                          {isOccupied && (
                            <div style={{ 
                              position: 'absolute', 
                              top: '-2px', 
                              right: '-2px',
                              width: '12px',
                              height: '12px',
                              backgroundColor: '#fff',
                              border: '1px solid #52c41a',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <UserOutlined style={{ fontSize: '8px', color: '#52c41a' }} />
                            </div>
                          )}
                        </div>
                      </Tooltip>
                    );
                  }) || (
                    // Fallback if no apartment units
                    <div style={{
                      width: '50px',
                      height: '40px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      color: '#999',
                      flexShrink: 0
                    }}>
                      -
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          )}

          {/* Legend */}
          <div style={{ 
            marginTop: '16px', 
            padding: '16px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '6px', 
            border: '1px solid #e9ecef'
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>Legend</h4>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '20px',
                  height: '16px',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px'
                }} />
                <span style={{ fontSize: '12px' }}>Vacant Unit</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '20px',
                  height: '16px',
                  backgroundColor: '#52c41a',
                  border: '1px solid #389e0d',
                  borderRadius: '4px',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-1px',
                    right: '-1px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid #52c41a',
                    borderRadius: '50%'
                  }} />
                </div>
                <span style={{ fontSize: '12px' }}>Occupied Unit</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UserOutlined style={{ fontSize: '14px', color: '#1890ff' }} />
                <span style={{ fontSize: '12px' }}>Click to assign/remove resident</span>
              </div>
            </div>
          </div>
        </Card>
      ),
    },
    {
      key: 'activityLog',
      label: t('common:activityLog'),
      children: (
        <Card>
          <ActivityLogList activityRecords={Array.isArray(blockData.activityRecords) ? blockData.activityRecords : []} />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/resources/properties/blocks')}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>{blockData.name}</Title>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('blocks.actions.edit')}
            </Button>
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => setShowDeleteModal(true)}
              loading={deleteMutation.isPending}
            >
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Tabs items={items} />

      <BlockFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleModalSubmit}
        initialValues={blockData}
        title={t('blocks.actions.edit')}
      />

      {/* Resident Selector Modal */}
      <Modal
        title={`Assign Resident to Unit ${selectedApartmentUnit?.number} on ${selectedApartmentUnit?.floor}`}
        open={showResidentSelector}
        onCancel={() => {
          setShowResidentSelector(false);
          setSelectedApartmentUnit(null);
          setSearchTerm('');
        }}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ margin: 0, color: '#666' }}>
            Select a resident to assign to this apartment unit. Only unassigned residents are shown.
          </p>
          <Input
            placeholder="Search residents..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            style={{ marginTop: 12 }}
            prefix={<UserOutlined />}
            allowClear
          />
        </div>
        <ResidentListTab
          onSelect={handleResidentSelect}
          searchTerm={searchTerm}
          excludeAssigned={true}
        />
      </Modal>

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteError(undefined);
        }}
        onConfirm={handleDelete}
        onForceDelete={() => {
          if (id) {
            forceDeleteMutation.mutate(id);
          }
        }}
        title={t('blocks.messages.deleteConfirm')}
        error={deleteError}
        onErrorDismiss={() => setDeleteError(undefined)}
      />
    </div>
  );
}; 